/**
 * ProShip Direct v2 — the three badges this module repeats.
 *
 * Provenance is the one that carries the model: with every configuration falling back to a
 * ProShip default, a row is never "missing" — it is either the merchant's own or ours. Nothing
 * here is ever red, because nothing here blocks. Credentials are the exception and get their
 * own chip, since an account with no working credentials genuinely cannot ship.
 */
import React from 'react';
import { Box, Chip, Tooltip, IconButton, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  PURPLE, ORANGE, SUCCESS, SUCCESS_BG, WARNING, WARNING_BG, ERROR, ERROR_BG, PRIMARY_BLUE,
  SUBTLE, WHITE,
} from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { CREDENTIAL_STATE } from '../data/defaults';

const baseChipSx = {
  fontFamily: FONT,
  fontSize: SIZE['4xs'],
  fontWeight: WEIGHT.semibold,
  height: 20,
  borderRadius: '4px',
  '& .MuiChip-label': { px: 0.75 },
};

const tint = (color) => ({ ...baseChipSx, color, bgcolor: `${color}14` });

/**
 * Whose contract carries the shipment. ProShip nomenclature: a courier is either a ProShip
 * courier (we maintain it) or a Direct courier (the merchant brought the contract).
 *
 * ProShip is purple, Direct is orange, everywhere the two are distinguished — this chip, the
 * performance charts and tables, and the comparison on the marketing page. Two screens disagreeing
 * about which colour means which pool is worse than either choice.
 */
export function OwnerChip({ owner }) {
  const direct = owner === 'DIRECT' || owner === 'OWN';
  return <Chip size='small' label={direct ? 'Direct' : 'ProShip'} sx={tint(direct ? ORANGE : PURPLE)} />;
}

/**
 * Whether one configuration is the merchant's own or inherited.
 *
 * A symbol rather than a word: five of these sit side by side in the My Couriers row, and five
 * repetitions of "ProShip default" reads as five problems when it is the normal, working state.
 * The tick means yours; the shield means ProShip has it covered. Both are fine, which is why
 * neither is red.
 */
export function ProvenanceChip({ provided, label = 'This', clickable = false }) {
  return provided ? (
    <Tooltip title={`${label}: your own, uploaded for this account`}>
      <CheckCircleIcon sx={{ fontSize: 17, color: SUCCESS }} />
    </Tooltip>
  ) : (
    <Tooltip
      title={`${label}: running on the ProShip default. Nothing is broken — ${clickable ? 'click to upload your own.' : 'upload your own to override it.'}`}
    >
      <ShieldOutlinedIcon sx={{ fontSize: 16, color: SUBTLE }} />
    </Tooltip>
  );
}

/**
 * The provenance symbol plus the way to change it.
 *
 * The pencil is only offered on an inherited cell: that is the one a merchant lands on wanting to
 * do something. Where they already supplied their own, the tick stands alone and the setup page
 * stays one click away in the row menu.
 */
export function ProvenanceCell({ provided, label, to }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <ProvenanceChip provided={provided} label={label} />
      {!provided && to && (
        <Tooltip title={`Upload your own ${(label || '').toLowerCase()}`}>
          <IconButton component={Link} to={to} size='small' sx={{ p: '2px' }} aria-label={`Edit ${label}`}>
            <EditOutlinedIcon sx={{ fontSize: 14, color: SUBTLE }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

/**
 * The five setup points for one account, as symbols.
 *
 * A progress bar answers "how far along am I", which is the wrong question — nothing here is
 * incomplete, it is either the merchant's or ProShip's. The symbols answer the question they
 * actually have, which is "which ones are mine", and each names itself on hover.
 *
 * The rate card is the exception: it has no ProShip default, so a missing one warns.
 *
 * `labelled` names each parameter under its symbol. Bare icons tell a merchant how many are
 * theirs but not which, which is the half they can act on — so anywhere there is room for the
 * words, the words are shown.
 *
 * `spread` distributes them across the full width of whatever they sit in, rather than bunching
 * them at the left with dead space to the right. Only useful where the container is wide and the
 * five are the row's main content.
 *
 * `linkTo(dimension)` makes the outstanding ones clickable, straight to the page that completes
 * them. Only the outstanding ones: a tick has nothing to finish, so making it a link would spend
 * the affordance on the four-fifths of the row that needs no action and hide it on the fifth that
 * does. The label picks up the link colour on hover so the clickable ones read as clickable.
 */
export function SetupSymbols({ config, dimensions, labelled = false, spread = false, linkTo }) {
  const symbolFor = (d, provided, clickable) =>
    d.required && !provided ? (
      <Tooltip
        title={`${d.label}: not uploaded. There is no ProShip default for this — cost comparison stays blank for this account.${clickable ? ' Click to upload it.' : ''}`}
      >
        <WarningAmberOutlinedIcon sx={{ fontSize: 16, color: WARNING }} />
      </Tooltip>
    ) : (
      <ProvenanceChip provided={provided} label={d.label} clickable={clickable} />
    );

  /*
   * Spread lays the dimensions out as equal columns rather than spacing them apart.
   *
   * space-between distributes the GAPS, so a row's symbols sit wherever its own labels happen to
   * end — "Rate card" is nearly three times the width of "TAT" — and no two rows line up. Equal columns with
   * each symbol centred in its own slot align down the list by construction.
   */
  return (
    <Box
      sx={
        spread
          ? {
              display: 'grid',
              gridTemplateColumns: `repeat(${dimensions.length}, minmax(0, 1fr))`,
              alignItems: 'start',
              width: '100%',
              gap: 0.5,
            }
          : {
              display: 'flex',
              alignItems: labelled ? 'flex-start' : 'center',
              justifyContent: 'flex-start',
              gap: labelled ? 1.75 : 0.85,
              flexWrap: 'wrap',
            }
      }
    >
      {dimensions.map((d) => {
        const provided = config?.[d.key] === 'OWN';
        // Only what is outstanding gets a link; a tick has nothing left to complete.
        const to = !provided && linkTo ? linkTo(d) : null;
        const linkSx = to
          ? {
              textDecoration: 'none',
              cursor: 'pointer',
              '&:hover .dp-setup-label': { color: PRIMARY_BLUE, textDecoration: 'underline' },
            }
          : {};

        const content = labelled ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.15, minWidth: 0, ...linkSx }}>
            {symbolFor(d, provided, Boolean(to))}
            <Typography
              className='dp-setup-label'
              sx={{
                fontFamily: FONT,
                fontSize: SIZE['4xs'],
                color: SUBTLE,
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {d.short || d.label}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', ...linkSx }}>{symbolFor(d, provided, Boolean(to))}</Box>
        );

        /*
          THE TWO WRAPPERS MUST CENTRE IDENTICALLY, and getting this wrong is what kept the columns
          crooked after the grid went in. A <Link> grid item is blockified and stretches, so its
          child filled the cell and the symbol landed centred. The plain <Box> was a flex container
          whose child sized to its own content and packed to flex-start, so the symbol sat left.

          Which branch a dimension takes depends on whether it is already provided — only what is
          outstanding gets a link — so every row drifted by its own pattern of ticks and shields,
          and no two rows agreed. Same display, same centring, both branches.
        */
        const cellSx = { display: 'flex', justifyContent: 'center', minWidth: 0 };

        return to ? (
          <Link
            key={d.key}
            to={to}
            style={{ ...cellSx, textDecoration: 'none' }}
            aria-label={`Set up ${d.label}`}
          >
            {content}
          </Link>
        ) : (
          <Box key={d.key} sx={cellSx}>{content}</Box>
        );
      })}
    </Box>
  );
}

const CREDENTIAL_LABEL = {
  [CREDENTIAL_STATE.TESTED]: 'Tested',
  [CREDENTIAL_STATE.PENDING]: 'Not tested',
  [CREDENTIAL_STATE.FAILED]: 'Failed',
};

/** The one hard gate in this module: no working credentials, no shipping. */
export function CredentialChip({ state }) {
  const styleFor = {
    [CREDENTIAL_STATE.TESTED]: { color: SUCCESS, bgcolor: SUCCESS_BG },
    [CREDENTIAL_STATE.PENDING]: { color: WARNING, bgcolor: WARNING_BG },
    [CREDENTIAL_STATE.FAILED]: { color: ERROR, bgcolor: ERROR_BG },
  }[state] || { color: SUBTLE, bgcolor: WHITE };

  return <Chip size='small' label={CREDENTIAL_LABEL[state] || state} sx={{ ...baseChipSx, ...styleFor }} />;
}
